import { MemberLevel } from '@common/modules/settings/enums/member-level.enum'
import { MemberStatus } from '@common/modules/settings/enums/member-status.enum'

import { getProjectMembers } from './projects'
import type { MemberProjectRole } from './types/member-project-role.type'
import type { Project } from './types/project.types'

const normalizeEmail = (email: string | null | undefined) => (email ?? '').trim().toLowerCase()

type GetMemberProjectRolesParams = {
  projects: Project[]
  memberId: string
  memberEmail: string
  currentUserId: string
  currentUserEmail: string
  viewerIsWorkspaceAdmin: boolean
}

/**
 * Aggregates a member's role across the supplied projects.
 *
 * The backend exposes no single endpoint that returns every project for an
 * arbitrary member together with their per-project role, so we fan out across
 * the candidate projects, read each project's members, and keep only the ones
 * the target member actually belongs to.
 *
 * Per-project membership reads run in parallel; a single failing project is
 * skipped rather than failing the whole aggregation.
 */
export async function getMemberProjectRoles({
  projects,
  memberId,
  memberEmail,
  currentUserId,
  currentUserEmail,
  viewerIsWorkspaceAdmin,
}: GetMemberProjectRolesParams): Promise<MemberProjectRole[]> {
  const targetEmail = normalizeEmail(memberEmail)
  const viewerEmail = normalizeEmail(currentUserEmail)

  const matchesTarget = (candidateId: string, candidateEmail: string) =>
    candidateId === memberId || (targetEmail.length > 0 && normalizeEmail(candidateEmail) === targetEmail)

  const matchesViewer = (candidateId: string, candidateEmail: string) =>
    (currentUserId.length > 0 && candidateId === currentUserId) ||
    (viewerEmail.length > 0 && normalizeEmail(candidateEmail) === viewerEmail)

  const results = await Promise.all(
    projects.map(async (project): Promise<MemberProjectRole | null> => {
      try {
        const members = await getProjectMembers(project.id)
        const member = members.find((m) => matchesTarget(m.id, m.email))

        if (!member) {
          return null
        }

        const viewer = members.find((m) => matchesViewer(m.id, m.email))
        const canEdit =
          (viewer?.role === MemberLevel.ADMIN || viewerIsWorkspaceAdmin) && member.status !== MemberStatus.PENDING

        return {
          project,
          memberUserId: member.id,
          role: member.role,
          status: member.status,
          canEdit,
        }
      } catch (error) {
        console.error(`Failed to resolve member role for project ${project.id}:`, error)
        return null
      }
    })
  )

  return results.filter((entry): entry is MemberProjectRole => entry !== null)
}
